import { Fragment, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { UserMultiSelect } from './UserMultiSelect';
import { PushHistory } from './PushHistory';
import {
  callSendPushToUsers,
  type PushTarget,
  type SendPushResult,
} from '../../services/firebase';
import { DeepLinkField } from '../common/DeepLinkField';
import {
  DEEP_LINK_NONE,
  buildDeepLink,
  type ParsedDeepLink,
} from '../Banners/deepLinkTargets';
import type { FirestoreUser } from '../../types';

const TITLE_MAX = 50;
const BODY_MAX = 150;
const MAX_SELECTION = 100;

type TargetMode = 'all' | 'specific';

export function PushNotifications() {
  const { user } = useAuth();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetMode, setTargetMode] = useState<TargetMode>('all');
  const [selectedUsers, setSelectedUsers] = useState<FirestoreUser[]>([]);
  const [deepLink, setDeepLink] = useState<ParsedDeepLink>({
    mode: DEEP_LINK_NONE,
    id: '',
    customUrl: '',
  });

  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [lastResult, setLastResult] = useState<SendPushResult | null>(null);
  // Bump to force the history list to reload after a successful send.
  const [historyKey, setHistoryKey] = useState(0);

  const titleOver = title.length > TITLE_MAX;
  const bodyOver = body.length > BODY_MAX;
  const titleEmpty = title.trim().length === 0;
  const bodyEmpty = body.trim().length === 0;

  const specificNoUsers = targetMode === 'specific' && selectedUsers.length === 0;

  // Resolve the deep-link editor state into a stored string (or null). `error`
  // is set when the chosen mode has missing/invalid input so Send is blocked.
  const built = useMemo(() => buildDeepLink(deepLink), [deepLink]);
  const deepLinkError = built.error;

  // The compose Send button is disabled on any content/target/deep-link problem.
  const sendDisabled =
    titleEmpty ||
    bodyEmpty ||
    titleOver ||
    bodyOver ||
    specificNoUsers ||
    !!deepLinkError;

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  const recipientSummary =
    targetMode === 'all'
      ? 'All users'
      : `${selectedUsers.length} user${selectedUsers.length === 1 ? '' : 's'}`;

  const resolveTarget = (): PushTarget =>
    targetMode === 'all'
      ? 'all'
      : { userIds: selectedUsers.map((u) => u.uid) };

  const handleSendTestToSelf = async () => {
    if (!user?.uid) {
      toast.error('No current admin user');
      return;
    }
    if (titleEmpty || bodyEmpty) {
      toast.error('Title and body are required');
      return;
    }
    if (titleOver || bodyOver) {
      toast.error('Title or body exceeds the character limit');
      return;
    }
    setTesting(true);
    try {
      const result = await callSendPushToUsers({
        title: title.trim(),
        body: body.trim(),
        target: { userIds: [user.uid] },
        isTest: true,
        ...(built.value ? { deepLink: built.value } : {}),
      });
      setLastResult(result);
      setHistoryKey((k) => k + 1);
      toast.success(
        `Test sent: ${result.fcmSuccessCount} delivered, ${result.fcmFailureCount} failed`,
      );
    } catch (err) {
      console.error('Test push failed:', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to send test push',
      );
    } finally {
      setTesting(false);
    }
  };

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      const result = await callSendPushToUsers({
        title: title.trim(),
        body: body.trim(),
        target: resolveTarget(),
        ...(built.value ? { deepLink: built.value } : {}),
      });
      setLastResult(result);
      setHistoryKey((k) => k + 1);
      setShowConfirm(false);
      toast.success(
        `Sent to ${result.recipientUserCount} user${
          result.recipientUserCount === 1 ? '' : 's'
        }: ${result.fcmSuccessCount} delivered`,
      );
    } catch (err) {
      console.error('Push send failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to send push');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Push Notifications</h1>
        <p className="text-gray-500 mt-1">
          Compose and send a push notification to app users.
        </p>
      </div>

      {/* Compose form */}
      <div className="bg-white shadow rounded-lg p-6 space-y-5 max-w-2xl">
        {/* Title */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Title
            </label>
            <span
              className={`text-xs ${
                titleOver ? 'text-red-600 font-medium' : 'text-gray-400'
              }`}
            >
              {title.length}/{TITLE_MAX}
            </span>
          </div>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Notification title"
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
              titleOver ? 'border-red-400' : 'border-gray-300'
            }`}
          />
        </div>

        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Body
            </label>
            <span
              className={`text-xs ${
                bodyOver ? 'text-red-600 font-medium' : 'text-gray-400'
              }`}
            >
              {body.length}/{BODY_MAX}
            </span>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Notification body"
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
              bodyOver ? 'border-red-400' : 'border-gray-300'
            }`}
          />
        </div>

        {/* Target */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recipients
          </label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                checked={targetMode === 'all'}
                onChange={() => setTargetMode('all')}
                className="text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">All users</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                checked={targetMode === 'specific'}
                onChange={() => setTargetMode('specific')}
                className="text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">Specific users</span>
            </label>
          </div>

          {targetMode === 'specific' && (
            <div className="mt-3">
              <UserMultiSelect
                selected={selectedUsers}
                onChange={setSelectedUsers}
                maxSelection={MAX_SELECTION}
              />
            </div>
          )}
        </div>

        {/* Deep link */}
        <DeepLinkField
          value={deepLink}
          onChange={setDeepLink}
          inputClass={inputClass}
          labelClass={labelClass}
        />

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2 border-t">
          <Button
            variant="primary"
            onClick={() => setShowConfirm(true)}
            disabled={sendDisabled}
          >
            Send
          </Button>
          <Button
            variant="secondary"
            onClick={handleSendTestToSelf}
            loading={testing}
            disabled={
              titleEmpty || bodyEmpty || titleOver || bodyOver || !!deepLinkError
            }
          >
            Send test to me first
          </Button>
        </div>

        {/* Last result */}
        {lastResult && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm space-y-1">
            <p className="font-medium text-gray-900">Last send result</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-gray-600">
              <span>Recipients</span>
              <span className="text-gray-900">
                {lastResult.recipientUserCount}
              </span>
              <span>Notifications written</span>
              <span className="text-gray-900">
                {lastResult.notificationsWritten}
              </span>
              <span>Delivered</span>
              <span className="text-green-700">
                {lastResult.fcmSuccessCount}
              </span>
              <span>Failed</span>
              <span
                className={
                  lastResult.fcmFailureCount > 0
                    ? 'text-red-700'
                    : 'text-gray-900'
                }
              >
                {lastResult.fcmFailureCount}
              </span>
              <span>Pruned tokens</span>
              <span className="text-gray-900">
                {lastResult.prunedTokenCount}
              </span>
              {/* The count that reframes a bad-looking send: recipients who got
                  the in-app notification but no push, because they have no
                  registered device. Rendered only when the server actually
                  reported it — absent means "not measured", not zero. */}
              {lastResult.recipientsWithNoDevice !== undefined && (
                <>
                  <span title="Recipients with no registered device — they received the in-app notification only.">
                    No device
                  </span>
                  <span
                    className={
                      lastResult.recipientsWithNoDevice > 0
                        ? 'text-amber-700'
                        : 'text-gray-900'
                    }
                  >
                    {lastResult.recipientsWithNoDevice}
                  </span>
                </>
              )}
              {lastResult.platformBreakdown !== undefined &&
                Object.entries(lastResult.platformBreakdown)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([platform, counts]) => (
                    <Fragment key={platform}>
                      <span className="pl-3 text-xs">
                        {platform === 'ios'
                          ? 'iOS'
                          : platform === 'android'
                          ? 'Android'
                          : platform}
                      </span>
                      <span className="text-xs text-gray-900">
                        {counts.success} delivered
                        {counts.failure > 0 && (
                          <span className="text-red-700">
                            {' '}
                            · {counts.failure} failed
                          </span>
                        )}
                        {counts.pruned > 0 && (
                          <span className="text-amber-700">
                            {' '}
                            · {counts.pruned} pruned
                          </span>
                        )}
                      </span>
                    </Fragment>
                  ))}
              <span>Log entry</span>
              <span className="text-gray-900 font-mono text-xs">
                {lastResult.logId}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* History */}
      <PushHistory reloadKey={historyKey} />

      {/* Confirmation modal */}
      <Modal
        isOpen={showConfirm}
        onClose={() => !sending && setShowConfirm(false)}
        title="Send push notification?"
        size="md"
      >
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              This will send to{' '}
              <span className="font-semibold">{recipientSummary}</span>.
            </p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="text-xs uppercase tracking-wide text-gray-400">
              Preview
            </p>
            <p className="font-semibold text-gray-900">{title}</p>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{body}</p>
            {built.value && (
              <p className="text-xs text-gray-400 font-mono pt-1">
                Deep link → {built.value}
              </p>
            )}
          </div>

          {targetMode === 'specific' && (
            <div className="text-xs text-gray-500 max-h-32 overflow-y-auto">
              {selectedUsers.map((u) => (
                <div key={u.uid}>{u.email || u.uid}</div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button
              variant="secondary"
              onClick={() => setShowConfirm(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmSend}
              loading={sending}
            >
              Confirm &amp; Send
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
